// @flow
import React, { Component, Fragment } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader, Button } from 'reactstrap';
import isEqual from 'lodash/isEqual';

import api from '../../lib/api';
import IconButton from '../Buttons/IconButton';
import { addEvent, removeEvent } from '../../lib/utils';
import { getToastMsg } from '../../components/ErrorMessage';

const ENTER_KEY = 13;

type createableSelectEntry = {
  label: string,
  value: string,
};
type msgModal = {
  isOpen: boolean,
  head: string,
  body?: string,
  footer: any,
};

type Props = {
  endpoint: string,
  patchKey: string,
  value: string | Array<string> | Object | number,
  className: string,
  children: any,
  multiline: boolean,
  patchObjDataType?: 'number' | 'array' | 'object',
  patchObjSchema?: string | Array<string>,
  schemaObjKeys: Array<string>,
  validate?: mixed,
  placeholder?: string,
  isCreatableSelect?: boolean,
};

type State = {
  isEditing: boolean,
  editButtonIsVisible: boolean,
  saveButtonIsVisible: boolean,
  inputValue: string | Array<createableSelectEntry | string>,
  msgModal: msgModal,
  // creatableInputValue: string,
};

const msgModalIniitalState = {
  isOpen: false,
  head: '',
  body: '',
  footer: null,
};

// TODO: who sees edit button OR write this logic somewhere in a render props and dont render EditableInput at all
// TODO: type annotate  editorRef and inputRef
// TODO: Handle enter press, also in multiline (Maybe save if double enter)

export default class EditableInput extends Component<Props, State> {
  state = {
    isEditing: false,
    editButtonIsVisible: true,
    saveButtonIsVisible: false,
    inputValue: this.props.isCreatableSelect ? [] : '',
    msgModal: msgModalIniitalState,
    // creatableInputValue: '',
  };

  editorRef = React.createRef();
  inputRef = React.createRef();

  static defaultProps = {
    placeholder: '',
  };

  render() {
    return (
      <div className={this.props.className} ref={this.editorRef}>
        {this.renderButtons()}
        {this.state.isEditing ? this.renderInput() : this.renderChildren()}
        {this.state.msgModal.isOpen && this.renderMsgModal()}
      </div>
    );
  }

  renderInput = () => {
    const inputAttributes = {
      value: this.state.inputValue,
      onChange: this.handleInputChange,
      ref: this.inputRef,
      placeholder: this.props.placeholder ? this.props.placeholder : '',
    };

    if (this.props.multiline) {
      return <textarea {...inputAttributes} cols="40" rows="7" />;
    } else {
      return <input {...inputAttributes} />;
    }
  };

  renderChildren = () => this.props.children;

  renderMsgModal = () => {
    const { isOpen, head, body, footer } = this.state.msgModal;

    return (
      <Modal isOpen={isOpen} toggle={this.closeMsgModal}>
        <ModalHeader toggle={this.closeMsgModal}>{head}</ModalHeader>
        {body && <ModalBody>{body}</ModalBody>}
        <ModalFooter>{footer}</ModalFooter>
      </Modal>
    );
  };

  renderButtons = () => {
    // SAVE BUTTON IS CHECKED AND RENDERED FIRST
    if (this.state.saveButtonIsVisible && this.hasInputChanged()) {
      return (
        <IconButton text="Save" iconClass="edit-pencil" onEditPress={this.onSavePress} />
      );
    }
    // RENDER EDIT BUTTON ON HOVER IF NOT ALREADY EDITING
    if (this.state.editButtonIsVisible && !this.state.isEditing) {
      return (
        <IconButton text="Edit" iconClass="edit-pencil" onEditPress={this.onEditPress} />
      );
    }
    return null;
  };

  handleInputChange = e => this.setState({ inputValue: e.target.value });

  hasInputChanged = () => {
    const { inputValue } = this.state;
    if (!isEqual(this.getInputValueFromProps(), inputValue)) return true;
    return false;
  };

  restructureData = data => {
    /**
     * Format Data Correctly Before Sending To Api.
     */
    const { patchObjDataType, patchObjSchema } = this.props;

    if (patchObjDataType) {
      //  IF data needs to be a number.
      if (patchObjDataType === 'number') {
        return Number(data);
      }

      // if data needs to be an array
      if (patchObjDataType === 'array') {
        // array of strings
        if (patchObjSchema) {
          if (typeof patchObjSchema === 'string') {
            return data.split(',').map(entry => {
              return {
                [patchObjSchema]: entry,
              };
            });
          } else if (typeof patchObjSchema === 'object') {
            // TODO: IF ITS ARRAY OF OBJECTS???
          }
        } else {
          return data.split(',').filter(val => val);
        }
      }
      // if data needs to be an Object
      if (patchObjDataType === 'object') {
        if (typeof patchObjSchema === 'string') {
          // obj with single key to edit
          return {
            [patchObjSchema]: data,
          };
        }
        // TODO: Obj with multiple keys to edit needs inputs
      }
    } else {
      return data;
    }
  };

  onSavePress = async () => {
    const { endpoint, patchKey, updateData } = this.props;
    const { inputValue } = this.state;

    if (inputValue) {
      try {
        const { data } = await api.patch(endpoint, {
          [patchKey]: this.restructureData(inputValue),
        });
        // Validate that the Required change happend and show messages
        if (
          this.isExcludedForEqualityCheck() ||
          isEqual(data[patchKey], this.restructureData(inputValue))
        ) {
          getToastMsg({ type: 'editProfile', status: 'success' });

          this.resetEditor();
          this.closeMsgModal();
          // this.setState({ savedValue: inputValue, });
          if (updateData) updateData(data);
        } else {
          this.openMsgModal({
            isOpen: true,
            head: 'Sorry! we could not update your profile.',
            footer: this.renderModalButtons('Try again'),
          });
        }
      } catch (e) {
        getToastMsg({ type: 'editProfile', status: 'error' });
        this.resetEditor();
        this.closeMsgModal();
      }
    } else {
      this.openMsgModal({
        isOpen: true,
        head: 'Please do not leave this field empty.',
        footer: this.renderModalButtons(),
      });
    }
  };

  onEnterPress = e => {
    if (e.keyCode === ENTER_KEY) {
      if (!this.props.multiline) {
        if (this.hasInputChanged()) {
          this.onSavePress();
        } else {
          this.resetEditor();
        }
      }
    }
  };

  showEditButton = () => this.setState({ editButtonIsVisible: true });
  hideEditButton = () => this.setState({ editButtonIsVisible: false });
  showSaveButton = () => this.setState({ saveButtonIsVisible: true });
  hideSaveButton = () => this.setState({ saveButtonIsVisible: false });

  onEditPress = () => {
    this.setState({
      isEditing: true,
      saveButtonIsVisible: true,
    });

    // Get values to show in input
    this.setState({
      inputValue: this.getInputValueFromProps(),
    });

    // Focus is called after 100ms expecting renderInput is called by then
    // and the Input is assigned to this.inputRef. Maybe more time is needed?
    setTimeout(() => {
      this.inputRef.current && this.inputRef.current.focus();
    }, 100);

    // Add Event Listener to Detect Enter press and outside Press.
    addEvent({
      elem: window,
      type: 'click',
      eventHandle: this.onOutsideEditorClick,
      options: true,
    });
    addEvent({
      elem: window,
      type: 'keyup',
      eventHandle: this.onEnterPress,
      options: true,
    });
  };

  onOutsideEditorClick = e => {
    // IF USER CLICKED OUTSIDE THE COMPONENT
    if (!this.editorRef.current.contains(e.target)) {
      if (this.hasInputChanged() && !this.state.msgModal.isOpen) {
        this.openMsgModal({
          isOpen: true,
          head: 'Do you want to save the changes you made?',
          footer: this.renderModalButtons('Save'),
        });
      } else {
        this.resetEditor();
      }
    }
  };

  openMsgModal = (args: msgModal) => {
    this.setState({
      msgModal: { ...args },
    });
  };

  closeMsgModal = () => this.setState({ msgModal: msgModalIniitalState });

  resetEditor = () => {
    this.setState({ isEditing: false, saveButtonIsVisible: false });

    removeEvent({
      elem: window,
      type: 'click',
      eventHandle: this.onOutsideEditorClick,
      options: true,
    });
    removeEvent({
      elem: window,
      type: 'keyup',
      eventHandle: this.onEnterPress,
      options: true,
    });
  };

  onCancelPress = () => {
    this.closeMsgModal();
    this.resetEditor();
  };

  renderModalButtons = (primaryBtnTxt?: string) => {
    if (!primaryBtnTxt)
      return (
        <Button color="secondary" onClick={this.onCancelPress}>
          Cancel
        </Button>
      );
    return (
      <Fragment>
        <Button color="primary" onClick={this.onSavePress}>
          {primaryBtnTxt}
        </Button>
        <Button color="secondary" onClick={this.onCancelPress}>
          Cancel
        </Button>
      </Fragment>
    );
  };

  isExcludedForEqualityCheck = () => {
    // Add Exclusions for all types of patchObjDataType = 'object' here.
    // because Sometimes the data sent in patch request is not same as
    // the data returned by the server if request is successful in case of objects
    // with more that one keys.

    const { patchObjSchema } = this.props;
    if (patchObjSchema === 'amount') return true;

    return false;
  };

  getInputValueFromProps = () => {
    const { value, patchObjDataType, patchObjSchema } = this.props;

    switch (patchObjDataType) {
      case 'number':
        return value;
      case 'array':
        return value.join(',');
      case 'object':
        if (typeof patchObjSchema === 'string') {
          return value[patchObjSchema];
        }
        return {};
      // TODO: handle cases for object types
      default:
        break;
    }

    return value;
  };
}



// WEBPACK FOOTER //
// ./src/components/EditableInput/EditableInput.jsx